// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CatalogGeneralInfo } from "./Models/catalog-general-info";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import { setConfirmation } from "../app/confirmation-slice";
import {
  Button,
  Checkbox,
  ComboBox,
  ProgressRadial,
} from "@itwin/itwinui-react";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import { ConfigManager } from "../Helpers/config-manager";
import { isXmlContent } from "../Helpers/helper-functions";
import { ProjectArchitectDetail } from "./Models/project-architect-detail";
import TextAreaComponent from "../CustomInputComponents/TextInputComponents/TextAreaComponent";
import { IntegrationProjectStage } from "./Models/integration-project-stage";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { ProjectApiHelper } from "./project-api-helper";
import { buildSaveProjectDto } from "./Helpers/save-project-dto-builder";
import {
  ExtractedVariable,
  extractVariablesFromXml,
  VariableValidationRule,
  VariableValidationType,
} from "./Helpers/xml-variable-extractor";
import {
  COPY_NAME_SUFFIX,
  COPY_UNIQUE_NAME_SUFFIX,
  withCopySuffix,
} from "./Helpers/duplicate-name";
import VariableValidationEditor from "./VariableValidationEditor";

export type ProjectFormProps = {
  project?: CatalogGeneralInfo;
  isEditable: boolean;
  isDuplicate: boolean;
  closeFrollFunction?: (refreshList: boolean) => void;
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  setInforPanelHeaderFunction?: (header: string) => void;
};

const XML_EXTRACTION_DEBOUNCE_MS = 300;

const projectStageMap: { [key: string]: IntegrationProjectStage } = {
  Development: IntegrationProjectStage.Development,
  Preview: IntegrationProjectStage.Preview,
  Current: IntegrationProjectStage.Current,
  Obsolete: IntegrationProjectStage.Obsolete,
};

const ProjectForm = (props: ProjectFormProps) => {
  const dispatch = useAppDispatch();
  const isDuplicate = props.isDuplicate;
  const isUpdate = props.project !== undefined && !isDuplicate;
  const { showToast } = useToastNotificationService();

  const [isLoading, setLoading] = useState<boolean>(
    props.project !== undefined,
  );

  const accessToken = useAppSelector(selectAccessToken);

  const [project, setProject] = useState<ProjectArchitectDetail>();
  const [name, setName] = useState<string>();
  const [uniqueName, setUniqueName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [version, setVersion] = useState<string>();
  const [inputExample, setInputExample] = useState<string>();
  const [responseExample, setResponseExample] = useState<string>();
  const [compiledXml, setCompiledXml] = useState<string>();
  const [projectId, setProjectId] = useState<string>();
  const [previousVersionId, setPreviousVersionId] = useState<string>();
  const [newVersionId, setNewVersionId] = useState<string>();
  const [isOdata, setIsOdata] = useState<boolean>(false);
  const [allowResultReuse, setAllowResultReuse] = useState<boolean>(false);
  const [odataSchema, setOdataSchema] = useState<string>();
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [subscribtionNotReq, setSubscribtionNotReq] = useState<boolean>(false);
  const [projectStage, setProjectStage] = useState<IntegrationProjectStage>(
    IntegrationProjectStage.Development,
  );

  const [extractedVariables, setExtractedVariables] = useState<ExtractedVariable[]>([]);
  const [reservedVariables, setReservedVariables] = useState<ExtractedVariable[]>([]);
  const [validationRules, setValidationRules] = useState<VariableValidationRule[]>([]);
  const [initialValidationRules, setInitialValidationRules] = useState<VariableValidationRule[]>([]);
  const [areValidationRulesValid, setAreValidationRulesValid] = useState<boolean>(true);
  const [isExtractingVariables, setIsExtractingVariables] = useState(false);
  const xmlDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);
  const [isForIntegrationTest, setIsForIntegrationTest] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<{
    [key: string]: "positive" | "warning" | undefined;
  }>({});

  const abortControllerRef = useRef<AbortController | null>(null);

  const normalizeRuleSet = useCallback((rules: VariableValidationRule[]) => {
    return [...rules]
      .filter(
        (r) => r.validationTypeId !== VariableValidationType.None || r.isRequired,
      )
      .map((r) => ({
        variableName: r.variableName,
        validationTypeId: r.validationTypeId,
        isRequired: r.isRequired,
        maxLength: r.maxLength,
        minValue: r.minValue,
        maxValue: r.maxValue,
        allowDecimals: r.allowDecimals,
        regexPattern: r.regexPattern,
        regexDescription: r.regexDescription,
        dateFormat: r.dateFormat,
        allowedValues: r.allowedValues,
      }))
      .sort((a, b) => a.variableName.localeCompare(b.variableName));
  }, []);

  useEffect(() => {
    const fetchProject = async (projId: string) => {
      abortControllerRef.current = new AbortController();
      const runId = crypto.randomUUID();
      const result = await ProjectApiHelper.fetchProjectData({
        projId,
        accessToken,
        dispatch,
        runId,
        signal: abortControllerRef.current.signal,
      });
      if (result.error) {
        const isAbort =
          result.error instanceof Error && result.error.name === "AbortError";
        if (!isAbort) {
          showToast({
            status: "negative",
            message: `An error occurred while fetching project data. ActivityId=\"${runId}\"`,
          });
        }
        setLoading(false);
        return;
      }
      const { projectDto } = result;
      if (projectDto) {
        setProject(projectDto);
        setName(
          isDuplicate
            ? withCopySuffix(projectDto.Name, COPY_NAME_SUFFIX)
            : projectDto.Name,
        );
        setUniqueName(
          isDuplicate
            ? withCopySuffix(projectDto.UniqueName, COPY_UNIQUE_NAME_SUFFIX)
            : projectDto.UniqueName,
        );
        setDescription(projectDto.Description);
        setVersion(projectDto.Version);
        setInputExample(projectDto.InputInfo?.InputExample);
        setResponseExample(projectDto.InputInfo?.ResponseExample);
        setCompiledXml(projectDto.Data?.CompiledXml);
        const { configurable, reserved } = extractVariablesFromXml(projectDto.Data?.CompiledXml || "");
        setExtractedVariables(configurable);
        setReservedVariables(reserved);
        setValidationRules(projectDto.VariableValidations ?? []);
        setInitialValidationRules(projectDto.VariableValidations ?? []);
        // A duplicate is a new project, not a new version of the source.
        setPreviousVersionId(isDuplicate ? undefined : projectDto.PrevVersionId);
        setNewVersionId(isDuplicate ? undefined : projectDto.NewVersionId);
        setIsOdata(projectDto.IsOdata);
        setAllowResultReuse(projectDto.AllowResultReuse);
        setIsEnabled(projectDto.IsEnabled);
        setSubscribtionNotReq(projectDto.SubscriptionNotRequired);
        setIsForIntegrationTest(projectDto.IsForIntegrationTest);
        setProjectStage(isDuplicate ? IntegrationProjectStage.Development : projectStageMap[projectDto.ProjectStageId]);
        setProjectId(isDuplicate ? undefined : projectDto.Id);
        setOdataSchema(projectDto.Data?.ODataSchema);
      }
      setLoading(false);
    };

    if (props.project !== undefined) {
      setLoading(true);
      void fetchProject(props.project.Id);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (xmlDebounceTimerRef.current) {
        clearTimeout(xmlDebounceTimerRef.current);
      }
    };
  }, [accessToken, dispatch, isDuplicate, props.project, showToast]);

  const handleStatusChange = (
    id: string,
    status: "positive" | "warning" | undefined,
  ) => {
    setStatuses((prevStatuses) => {
      if (prevStatuses[id] !== status) {
        return {
          ...prevStatuses,
          [id]: status,
        };
      }
      return prevStatuses;
    });
  };

  const { setInforPanelHeaderFunction } = props;
  useEffect(() => {
    setInforPanelHeaderFunction?.(`${isUpdate ? "Edit" : "New"} project`);
  }, [isUpdate, setInforPanelHeaderFunction]);

  useMemo(() => {
    if (name && uniqueName && version && compiledXml && projectStage) {
      if (isOdata) {
        setIsFormValid(odataSchema !== undefined && odataSchema !== "");
      } else {
        setIsFormValid(true);
      }
    } else {
      setIsFormValid(false);
    }
  }, [
    name,
    uniqueName,
    version,
    compiledXml,
    projectStage,
    isOdata,
    odataSchema,
  ]);

  const wasFormChanged = useCallback(() => {
    if (project !== undefined) {
      if (
        project.Name === name &&
        project.UniqueName === uniqueName &&
        project.Description === description &&
        project.Version === version &&
        project.InputInfo?.InputExample === inputExample &&
        project.InputInfo?.ResponseExample === responseExample &&
        project.Data?.CompiledXml === compiledXml &&
        project.PrevVersionId === previousVersionId &&
        project.NewVersionId === newVersionId &&
        project.IsOdata === isOdata &&
        project.AllowResultReuse === allowResultReuse &&
        (odataSchema ?? "") === (project.Data?.ODataSchema ?? "") &&
        project.IsEnabled === isEnabled &&
        project.SubscriptionNotRequired === subscribtionNotReq &&
        projectStageMap[project.ProjectStageId] === projectStage &&
        JSON.stringify(normalizeRuleSet(validationRules)) ===
          JSON.stringify(normalizeRuleSet(initialValidationRules)) &&
        project.IsForIntegrationTest === isForIntegrationTest
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (
        name ||
        uniqueName ||
        description ||
        version ||
        inputExample ||
        responseExample ||
        compiledXml ||
        previousVersionId ||
        newVersionId ||
        isOdata ||
        odataSchema ||
        isEnabled ||
        subscribtionNotReq ||
        projectStage !== IntegrationProjectStage.Development ||
        isForIntegrationTest
      ) {
        return true;
      } else {
        return false;
      }
    }
  }, [
    name,
    uniqueName,
    description,
    version,
    inputExample,
    responseExample,
    compiledXml,
    previousVersionId,
    newVersionId,
    isOdata,
    allowResultReuse,
    odataSchema,
    isEnabled,
    subscribtionNotReq,
    projectStage,
    validationRules,
    initialValidationRules,
    normalizeRuleSet,
    project,
    isForIntegrationTest,
  ]);

  const allInputStatusesValid = useMemo(
    () => Object.values(statuses).every((status) => status !== "warning"),
    [statuses],
  );

  useMemo(() => {
    const isFormChanged = wasFormChanged();
    setIsFormDataChanged(isFormChanged);
  }, [wasFormChanged]);

  function getSaveJson() {
    const saveProjectDto = buildSaveProjectDto(
      {
        name: name as string,
        uniqueName: uniqueName as string,
        description,
        version: version as string,
        inputExample,
        responseExample,
        compiledXml: compiledXml as string,
        projectStage,
        newVersionId,
        previousVersionId,
        isOdata,
        allowResultReuse,
        isEnabled,
        subscriptionNotRequired: subscribtionNotReq,
        isForIntegrationTest,
        validationRules,
      },
      {
        isUpdate,
        sourceId: project?.Id,
        includeValidations: ConfigManager.isProjectVariableValidationEnabled,
      },
    );

    return JSON.stringify(saveProjectDto);
  }

  const saveProject = async () => {
    setLoading(true);
    const saveUrl = `${ConfigManager.projectProviderUrl}/api/Projects`;
    const jsonBody = getSaveJson();
    const saveRunId = crypto.randomUUID();

    try {
      const saveResponse = await fetch(saveUrl, {
        method: isUpdate ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: saveRunId,
        },
        body: jsonBody,
      });

      const saveData = await saveResponse.json();

      if (!saveResponse.ok || saveData.isSuccess === false) {
        let message: string =
          saveData.displayMessage ??
          saveData.title ??
          `An error occurred while saving the project.`;

        if (saveResponse.status === 422 && saveData.errors) {
          const errorLines: string[] = Array.isArray(saveData.errors)
            ? saveData.errors
            : Object.values(saveData.errors as Record<string, string[]>).flat();
          if (errorLines.length > 0) {
            message = errorLines.join(" | ");
          }
        }

        showToast({
          status: "negative",
          message: `${message} ActivityId="${saveRunId}"`,
        });
      } else {
        const schemaChanged = isDuplicate || odataSchema !== project?.Data?.ODataSchema;
        if (isOdata && odataSchema && schemaChanged) {
          const schemaUrl =
            `${ConfigManager.projectProviderUrl}/api/Projects/${encodeURIComponent(
              uniqueName ?? "",
            )}/schema`;
          const schemaRunId = crypto.randomUUID();

          const schemaResponse = await fetch(schemaUrl, {
            method: "POST",
            headers: {
              "Content-Type": isXmlContent(odataSchema)
                ? "application/xml"
                : "application/json",
              Authorization: accessToken,
              RunId: schemaRunId,
            },
            body: odataSchema,
          });

          // Failure bodies may be empty or non-JSON (e.g. 415/500).
          const schemaData = await schemaResponse.json().catch(() => ({}));

          if (
            !schemaResponse.ok ||
            schemaData.isSuccess === false ||
            schemaData.errors
          ) {
            const formattedErrors = schemaData.errors
              ? Object.entries(schemaData.errors)
                  .map(([key, messages]) => {
                    const field = key ? `${key}: ` : "";
                    return `${field}${(messages as string[]).join(" ")}`;
                  })
                  .join(", ")
              : undefined;
            const message =
              formattedErrors ??
              schemaData.displayMessage ??
              schemaData.title ??
              "The project was saved but the OData schema could not be saved.";
            showToast({
              status: "negative",
              message: `${message} ActivityId="${schemaRunId}"`,
            });
            return;
          }
        }

        showToast({ status: "positive", action: isUpdate ? "update" : "create" });
        closeFRoll(true);
      }
    } catch {
      showToast({
        status: "negative",
        message: `An error occurred while saving the project. ActivityId="${saveRunId}"`,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (refreshList: boolean) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList);
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    await saveProject();
  };

  const integrationProjectStageOptions = [
    { value: IntegrationProjectStage.Development, label: "Development" },
    { value: IntegrationProjectStage.Preview, label: "Preview" },
    { value: IntegrationProjectStage.Current, label: "Current" },
    { value: IntegrationProjectStage.Obsolete, label: "Obsolete" },
  ];

  function getComboboxValue() {
    const savedValue = integrationProjectStageOptions.find(
      (o) => o.value === projectStage,
    )?.value;
    if (savedValue !== undefined) {
      return savedValue;
    } else {
      return integrationProjectStageOptions[0].value;
    }
  }

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div style={{ height: "100%" }}>
          <form onSubmit={handleSubmit} style={{ height: "100%" }}>
            <fieldset
              disabled={!props.isEditable}
              style={{
                border: 0,
                padding: 0,
                margin: 0,
                minWidth: 0,
                height: "100%",
              }}
            >
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  className="becs-infopanelbody-footer-line"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                  }}
                >
                  <div className="becs-all-margin">
                    <LabeledInputComponent
                      required={true}
                      validationType={EfFieldValidationTypeEnum.Name150}
                      onStatusChange={(status: "positive" | "warning" | undefined) =>
                        handleStatusChange("name", status)
                      }
                      displayStyle="default"
                      label="Name"
                      placeholder="Enter text here..."
                      maxLength={150}
                      key="LbName"
                      defaultValue={name}
                      onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                        setName(currentValue.target.value);
                      }}
                    />

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        required={true}
                        validationType={EfFieldValidationTypeEnum.UniqueName}
                        onStatusChange={(status: "positive" | "warning" | undefined) =>
                          handleStatusChange("uniqueName", status)
                        }
                        displayStyle="default"
                        label="Unique name"
                        placeholder="Enter text here..."
                        maxLength={150}
                        key="LbUniqueName"
                        defaultValue={uniqueName}
                        onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                          setUniqueName(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        required={true}
                        validationType={EfFieldValidationTypeEnum.Version}
                        onStatusChange={(status: "positive" | "warning" | undefined) =>
                          handleStatusChange("version", status)
                        }
                        displayStyle="default"
                        label="Version"
                        placeholder="Enter text here..."
                        maxLength={50}
                        key="LbVersion"
                        defaultValue={version}
                        onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                          setVersion(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <div className="becs-input-grid">
                        <div className={"becs-input-label required"}>
                          Project stage
                        </div>
                      </div>

                      <ComboBox
                        options={integrationProjectStageOptions}
                        onChange={(currentValue) => {
                          setProjectStage(
                            currentValue,
                          );
                        }}
                        value={getComboboxValue()}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        validationType={EfFieldValidationTypeEnum.Default}
                        onStatusChange={(status: "positive" | "warning" | undefined) =>
                          handleStatusChange("description", status)
                        }
                        displayStyle="default"
                        label="Description"
                        placeholder="Enter text here..."
                        maxLength={250}
                        key="LbDescription"
                        defaultValue={description}
                        onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                          setDescription(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <div className="becs-input-grid">
                        <div className="becs-input-label required">
                          Compiled project (XML)
                        </div>
                      </div>
                      <TextAreaComponent
                        required={true}
                        validationType={EfFieldValidationTypeEnum.Xml}
                        onStatusChange={(status) =>
                          handleStatusChange("compiledProject", status)
                        }
                        placeholder="Enter text here..."
                        style={{ height: 100 }}
                        defaultValue={compiledXml}
                        onChange={(currentValue) => {
                          const xml = currentValue.target.value;
                          setCompiledXml(xml);
                          if (xmlDebounceTimerRef.current) {
                            clearTimeout(xmlDebounceTimerRef.current);
                          }
                          setIsExtractingVariables(true);
                          xmlDebounceTimerRef.current = setTimeout(() => {
                            const { configurable, reserved } = extractVariablesFromXml(xml);
                            setExtractedVariables(configurable);
                            setReservedVariables(reserved);
                            setValidationRules((prev) =>
                              configurable.map((v) => {
                                const existing = prev.find(
                                  (r) => r.variableName === v.name,
                                );
                                return (
                                  existing ?? {
                                    variableName: v.name,
                                    validationTypeId: VariableValidationType.None,
                                    isRequired: false,
                                  }
                                );
                              })
                            );
                            setIsExtractingVariables(false);
                          }, XML_EXTRACTION_DEBOUNCE_MS);
                        }}
                      />
                      </div>
                      {compiledXml && ConfigManager.isProjectVariableValidationEnabled && (
                        <div className="becs-t-margin">
                          <div className="becs-input-grid">
                            <div className="becs-input-label">
                              Input Validation
                            </div>
                          </div>
                          <VariableValidationEditor
                            variables={extractedVariables}
                            reservedVariables={reservedVariables}
                            validationRules={validationRules}
                            onChange={setValidationRules}
                            isEditable={props.isEditable}
                            isLoading={isExtractingVariables}
                            projectStage={projectStage}
                            onValidityChange={setAreValidationRulesValid}
                          />
                        </div>
                      )}

                    <div className="becs-t-margin">
                      <div className="becs-input-grid">
                        <div className="becs-input-label">
                          Input example (JSON)
                        </div>
                      </div>
                      <TextAreaComponent
                        validationType={EfFieldValidationTypeEnum.Json}
                        onStatusChange={(status) =>
                          handleStatusChange("inputExample", status)
                        }
                        placeholder="Enter text here..."
                        style={{ height: 100 }}
                        defaultValue={inputExample}
                        onChange={(currentValue) => {
                          setInputExample(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <div className="becs-input-grid">
                        <div className="becs-input-label">Response example</div>
                      </div>
                      <TextAreaComponent
                        validationType={EfFieldValidationTypeEnum.Default}
                        onStatusChange={(status) =>
                          handleStatusChange("responseExample", status)
                        }
                        placeholder="Enter text here..."
                        style={{ height: 100 }}
                        defaultValue={responseExample}
                        onChange={(currentValue) => {
                          setResponseExample(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        validationType={EfFieldValidationTypeEnum.GuidType}
                        disabled={true} //projectid is server-assigned
                        displayStyle="default"
                        label="Project id"
                        maxLength={36}
                        key="LbProjectId"
                        defaultValue={projectId}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        validationType={EfFieldValidationTypeEnum.GuidType}
                        displayStyle="default"
                        label="Previous version project id"
                        maxLength={36}
                        key="LbPreviousVersionId"
                        defaultValue={previousVersionId}
                        onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                          setPreviousVersionId(currentValue.target.value);
                        }}
                      />
                    </div>

                    <div className="becs-t-margin">
                      <LabeledInputComponent
                        validationType={EfFieldValidationTypeEnum.GuidType}
                        displayStyle="default"
                        label="Newer version project id"
                        maxLength={36}
                        key="LbNewerVersionId"
                        defaultValue={newVersionId}
                        onChange={(currentValue: React.ChangeEvent<HTMLInputElement>) => {
                          setNewVersionId(currentValue.target.value);
                        }}
                      />
                    </div>
                    <div className="becs-t-margin">
                      <Checkbox
                        label={"Is Odata project"}
                        key={"CbIsOdata"}
                        onChange={(e) => {
                          setIsOdata(e.target.checked);
                        }}
                        defaultChecked={isOdata}
                      />
                    </div>
                    {isOdata && (
                      <div>
                        <div className="becs-t-margin">
                          <div className="becs-input-grid">
                            <div className="becs-input-label required">
                              New Odata Schema (xml/json)
                            </div>
                          </div>
                          <TextAreaComponent
                            required={true}
                            validationType={EfFieldValidationTypeEnum.XmlOrJson}
                            onStatusChange={(status) =>
                              handleStatusChange("odataSchema", status)
                            }
                            placeholder="Enter text here..."
                            style={{ height: 100 }}
                            defaultValue={odataSchema}
                            onChange={(currentValue) => {
                              setOdataSchema(currentValue.target.value);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="becs-t-margin">
                      <Checkbox
                        label={"Allow result reuse"}
                        key={"CbAllowResultReuse"}
                        onChange={(e) => {
                          setAllowResultReuse(e.target.checked);
                        }}
                        defaultChecked={allowResultReuse}
                      />
                    </div>
                    <div className="becs-t-margin">
                      <Checkbox
                        label={"Is enabled"}
                        key={"CbIsEnabled"}
                        onChange={(e) => {
                          setIsEnabled(e.target.checked);
                        }}
                        defaultChecked={isEnabled}
                      />
                    </div>
                    <div className="becs-t-margin">
                      <Checkbox
                        label={"Subscribtion NOT required"}
                        key={"CbSubscribtionNotRequired"}
                        onChange={(e) => {
                          setSubscribtionNotReq(e.target.checked);
                        }}
                        defaultChecked={subscribtionNotReq}
                      />
                    </div>
                    <div className="becs-t-margin">
                      <Checkbox
                        label={"Is for integration test"}
                        key={"CbIsForIntegrationTest"}
                        onChange={(e) => {
                          setIsForIntegrationTest(e.target.checked);
                        }}
                        defaultChecked={isForIntegrationTest}
                      />
                    </div>
                  </div>
                </div>
                {props.isEditable && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div className="becs-all-margin">
                      <div className="rowWrap">
                        <Button
                          key="saveCredentialBtn"
                          as="button"
                          styleType="cta"
                          type="submit"
                          disabled={
                            !(
                              isFormValid &&
                              isFormDataChanged &&
                              allInputStatusesValid &&
                              areValidationRulesValid
                            )
                          }
                        >
                          <div>
                            {!isUpdate ? "Save" : "Update"}
                          </div>
                        </Button>
                        <Button
                          key="cancelCredentialBtn"
                          as="button"
                          onClick={() => onCancelClick()}
                        >
                          <div>Cancel</div>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProjectForm;
